import { BatchEditCampaignPage } from 'components/BatchEditCampaign/BatchEditCampaignPage';
import { BatchEditCampaignSection } from 'components/BatchEditCampaign/BatchEditCampaignSection';
import { CustomField } from 'components/common/form/field/CustomField';
import { FormikSelectField } from 'components/common/form/field/SelectField';
import { FormikToggleButtonField } from 'components/common/form/field/ToggleButtonField';
import { ThirdPartyTrackingCodes } from 'components/ThirdPartyTrackingCode/ThirdPartyTrackingCodes';
import i18n from 'i18n';
import { isNil, noop } from 'lodash';
import { Formik, FormikProps } from 'formik';
import { useCallback } from 'react';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { addOnEnabled } from 'core/permission/PermissionDSL';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { FormObserver } from 'components/common/form/FormObserver';
import { Warning } from 'components/Warning/Warning';
import { ModifyTrackingStateContentModel } from './ModifyTrackingStateContentModel';
import { TrackingData } from 'core/rtbCampaign/TrackingData';

export const ModifyTrackingStateContent: React.FC<{
  model: () => ModifyTrackingStateContentModel;
}> = ({ model: useModel }) => {
  const {
    defaultTrackingData,
    conversionTrackingOptions,
    conversionTrackingTypeOptions,
    getThirdPartyTrackingTypeOptions,
    getThirdPartyTriggerTypeOptions,
    onDataChange
  } = useModel();

  const renderFormContent = useCallback(
    ({ values }: FormikProps<TrackingData>) => {
      const conversionTracking = values.conversionTracking;
      return (
        <>
          {/* 為了要讓 ModifyTrackingState 判斷能不能到下一步，
          使用者有變動 value 就要同步回 context data */}
          <FormObserver onChange={onDataChange} />
          <PermissionChecker
            permissionAware={addOnEnabled(
              ADDONFEATURE.CONVERSION_TRACKING.CONV_TRACKING_LIST
            )}
          >
            <BatchEditCampaignSection
              title={i18n.t('campaign.strategyAndTracking.conversionTracking')}
            >
              <div style={{ padding: '30px' }}>
                <FormikSelectField
                  name='conversionTracking'
                  label={i18n.t(
                    'campaign.strategyAndTracking.conversionTracking'
                  )}
                  options={conversionTrackingOptions}
                  isClearable={true}
                />
                {!isNil(conversionTracking) &&
                  conversionTrackingTypeOptions &&
                  conversionTrackingTypeOptions.length > 0 && (
                    <FormikToggleButtonField
                      label={i18n.t('campaign.labels.convTrackEvent')}
                      name='convTrackEvent'
                      options={conversionTrackingTypeOptions}
                    />
                  )}
              </div>
            </BatchEditCampaignSection>
          </PermissionChecker>
          <PermissionChecker
            permissionAware={addOnEnabled(
              ADDONFEATURE.CAMPAIGN.THIRDPARTY_TRACKING
            )}
          >
            <BatchEditCampaignSection
              title={i18n.t('campaign.strategyAndTracking.thirdPartyTracking')}
            >
              <div style={{ padding: '30px' }}>
                <CustomField
                  name='viewTrackingCodesContainer'
                  label={i18n.t(
                    'campaign.strategyAndTracking.thirdPartyTracking'
                  )}
                  hint={
                    <Warning>
                      {i18n.t('campaign.strategyAndTracking.asiaMaxOnlyHint')}
                    </Warning>
                  }
                >
                  <ThirdPartyTrackingCodes
                    getTrackingTypeOptions={getThirdPartyTrackingTypeOptions}
                    getTriggerTypeOptions={getThirdPartyTriggerTypeOptions}
                  />
                </CustomField>
              </div>
            </BatchEditCampaignSection>
          </PermissionChecker>
        </>
      );
    },
    [
      conversionTrackingOptions,
      conversionTrackingTypeOptions,
      getThirdPartyTrackingTypeOptions,
      getThirdPartyTriggerTypeOptions,
      onDataChange
    ]
  );

  return (
    <BatchEditCampaignPage title={i18n.t('modifyTrackingState.title')}>
      <Formik
        initialValues={defaultTrackingData}
        enableReinitialize
        onSubmit={onDataChange}
        validate={noop}
        validateOnBlur={false}
      >
        {renderFormContent}
      </Formik>
    </BatchEditCampaignPage>
  );
};
